/**
 * Drop-in replacement for lucide-react that renders Phosphor icons with
 * `weight="fill"` so all icons across the system look solid/filled.
 *
 * Usage stays identical to lucide-react:
 *   import { User, Calendar as CalendarIcon, Trash2 } from "@/components/icons";
 *   <User size={20} className="text-black" />
 *
 * Internally each export is a small wrapper that swallows lucide-only props
 * (`strokeWidth`, `absoluteStrokeWidth`, `fill`) and forces `weight="fill"`.
 */
import React from "react";
import * as Ph from "@phosphor-icons/react";

// Factory: returns a React component wrapping a Phosphor icon and forcing
// `weight="fill"` by default. Lucide-only props are discarded so callers can
// migrate without touching their JSX.
const make = (PhComponent, defaultWeight = "fill") => {
  const Wrapped = React.forwardRef(
    (
      // eslint-disable-next-line no-unused-vars
      { size = 20, color, className, strokeWidth, weight, absoluteStrokeWidth, fill, ...rest },
      ref,
    ) => (
      <PhComponent
        ref={ref}
        size={size}
        color={color}
        weight={weight || defaultWeight}
        className={className}
        {...rest}
      />
    ),
  );
  Wrapped.displayName = PhComponent.displayName || "Icon";
  return Wrapped;
};

// Direct name-equivalents
export const Activity = make(Ph.Pulse);
export const ArrowLeft = make(Ph.ArrowLeft);
export const ArrowRight = make(Ph.ArrowRight);
export const ArrowUpRight = make(Ph.ArrowUpRight);
export const Bell = make(Ph.Bell);
export const BellOff = make(Ph.BellSlash);
export const Calendar = make(Ph.Calendar);
export const CalendarCheck = make(Ph.CalendarCheck);
export const Camera = make(Ph.Camera);
export const Check = make(Ph.Check);
export const CheckCircle = make(Ph.CheckCircle);
export const CheckCircle2 = make(Ph.CheckCircle);
export const Circle = make(Ph.Circle);
export const Clock = make(Ph.Clock);
export const CreditCard = make(Ph.CreditCard);
export const Database = make(Ph.Database);
export const Eye = make(Ph.Eye);
export const File = make(Ph.File);
export const FileImage = make(Ph.FileImage);
export const FileText = make(Ph.FileText);
export const FileType = make(Ph.FileText);
export const FolderOpen = make(Ph.FolderOpen);
export const Globe = make(Ph.Globe);
export const Heart = make(Ph.Heart);
export const Image = make(Ph.Image);
export const Info = make(Ph.Info);
export const Link2 = make(Ph.Link);
export const List = make(Ph.List);
export const MessageSquare = make(Ph.ChatText);
export const Lock = make(Ph.Lock);
export const MapPin = make(Ph.MapPin);
export const Minus = make(Ph.Minus);
export const Moon = make(Ph.Moon);
export const Paperclip = make(Ph.Paperclip);
export const Percent = make(Ph.Percent);
export const Phone = make(Ph.Phone);
export const Pill = make(Ph.Pill);
export const Play = make(Ph.Play);
export const Plus = make(Ph.Plus);
export const Printer = make(Ph.Printer);
export const Ruler = make(Ph.Ruler);
export const Shield = make(Ph.Shield);
export const ShieldCheck = make(Ph.ShieldCheck);
export const ShieldAlert = make(Ph.ShieldWarning);
export const Cake = make(Ph.Cake);
export const Microphone = make(Ph.Microphone);
export const IdCard = make(Ph.IdentificationCard);
export const ShoppingBag = make(Ph.ShoppingBag);
export const Stethoscope = make(Ph.Stethoscope);
export const Sun = make(Ph.Sun);
export const Thermometer = make(Ph.Thermometer);
export const User = make(Ph.User);
export const UserPlus = make(Ph.UserPlus);
export const Users = make(Ph.Users);
export const Wallet = make(Ph.Wallet);
export const Wind = make(Ph.Wind);
export const X = make(Ph.X);
export const XCircle = make(Ph.XCircle);

// Renamed equivalents (lucide → phosphor)
export const AlertCircle = make(Ph.WarningCircle);
export const AlertTriangle = make(Ph.Warning);
export const Ban = make(Ph.Prohibit);
export const Bandage = make(Ph.Bandaids);
export const Banknote = make(Ph.Money);
export const Building2 = make(Ph.Buildings);
export const CalendarClock = make(Ph.ClockCountdown);
export const CalendarDays = make(Ph.CalendarDots);
export const CalendarRange = make(Ph.CalendarBlank);
export const ChevronDown = make(Ph.CaretDown);
export const ChevronLeft = make(Ph.CaretLeft);
export const ChevronRight = make(Ph.CaretRight);
export const ChevronUp = make(Ph.CaretUp);
export const CornerDownRight = make(Ph.ArrowBendDownRight);
export const Download = make(Ph.DownloadSimple);
export const Droplet = make(Ph.Drop);
export const Droplets = make(Ph.Drop);
export const EyeOff = make(Ph.EyeSlash);
export const FlaskConical = make(Ph.Flask);
export const GripVertical = make(Ph.DotsSixVertical);
export const HeartPulse = make(Ph.Heartbeat);
export const Home = make(Ph.House);
export const Inbox = make(Ph.Tray);
export const LayoutDashboard = make(Ph.SquaresFour);
export const LayoutGrid = make(Ph.SquaresFour);
export const LifeBuoy = make(Ph.Lifebuoy);
export const Loader2 = make(Ph.CircleNotch);
export const LogOut = make(Ph.SignOut);
export const Mail = make(Ph.Envelope);
export const MailCheck = make(Ph.EnvelopeOpen);
export const MailWarning = make(Ph.WarningCircle);
export const MessageCircle = make(Ph.ChatCircle);
export const MoreHorizontal = make(Ph.DotsThree);
export const MoreVertical = make(Ph.DotsThreeVertical);
export const Pencil = make(Ph.PencilSimple);
export const PenLine = make(Ph.PencilLine);
export const RefreshCw = make(Ph.ArrowsClockwise);
export const Save = make(Ph.FloppyDisk);
export const Scale = make(Ph.Scales);
export const Search = make(Ph.MagnifyingGlass);
export const Send = make(Ph.PaperPlaneTilt);
export const Settings = make(Ph.Gear);
export const ShieldOff = make(Ph.Shield);
export const Sparkles = make(Ph.Sparkle);
export const Trash2 = make(Ph.Trash);
export const TrendingUp = make(Ph.TrendUp);
export const UserCog = make(Ph.UserGear);
export const Wrench = make(Ph.Wrench);
export const QrCode = make(Ph.QrCode);
export const ScanLine = make(Ph.Scan);

// Single-occurrence helpers
export const DotsSix = make(Ph.DotsSix);

export const DollarSign = make(Ph.CurrencyDollarSimple);
export const Mic = make(Ph.Microphone);
export const MicOff = make(Ph.MicrophoneSlash);
export const Ear = make(Ph.Ear);
export const Headphones = make(Ph.Headphones);
export const Pause = make(Ph.Pause);
export const Award = make(Ph.Medal);